import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Plus, LinkSimple, TreeStructure, ArrowUpRight, LinkBreak, ListBullets, CalendarBlank, Receipt } from "@phosphor-icons/react";
import SubJobsSortableList from "./SubJobsSortableList";
import SubJobsDayPlan from "./SubJobsDayPlan";
import SubJobDialog from "./SubJobDialog";
import AttachJobDialog from "./AttachJobDialog";
import { SubJobChip, SubJobProgressBar, isSubJob } from "./SubJobProgress";
import { fmtMoney } from "../quotes/PortalQuoteActions";

/* Running project value: approved variation quotes on every sub-job + the main job's own (ex VAT) */
function ProjectValue({ value }) {
  if (!value || (!value.approved_total && !value.pending_total)) return null;
  const parts = [
    `${value.sub_jobs_approved_count} approved sub-job quote${value.sub_jobs_approved_count === 1 ? "" : "s"} · ${fmtMoney(value.sub_jobs_approved_total)}`,
    value.parent_approved_total ? `main job variations · ${fmtMoney(value.parent_approved_total)}` : "",
    value.pending_total ? `${fmtMoney(value.pending_total)} awaiting client approval` : "",
  ].filter(Boolean);
  return (
    <div className="rounded-md border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50/70 dark:bg-emerald-950/20 px-3 py-1.5 text-right shrink-0" title={parts.join(" · ")} data-testid="sub-jobs-project-value">
      <p className="text-[9px] uppercase tracking-widest font-semibold text-emerald-700/80 dark:text-emerald-300/80 inline-flex items-center gap-1"><Receipt size={10} weight="fill" /> Approved value</p>
      <p className="text-sm font-bold font-[Chivo] tabular-nums text-emerald-800 dark:text-emerald-200" data-testid="sub-jobs-project-value-approved">{fmtMoney(value.approved_total)} <span className="text-[10px] font-normal text-muted-foreground">ex VAT</span></p>
      {value.pending_total > 0 && <p className="text-[10px] text-amber-700 dark:text-amber-300 tabular-nums" data-testid="sub-jobs-project-value-pending">+ {fmtMoney(value.pending_total)} awaiting approval</p>}
    </div>
  );
}

/* Sub-job banner shown on a sub-job's own page */
function ParentBanner({ job, onChanged }) {
  const p = job.parent_job || {};
  const detach = async () => {
    if (!window.confirm(`Detach ${job.reference} from main job ${p.reference || job.parent_job_reference}? It becomes a standalone job again.`)) return;
    try {
      const { data } = await API.post(`/jobs/${job.parent_job_id}/sub-jobs/${job.id}/detach`);
      toast.success(data.message); onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not detach"); }
  };
  return (
    <div className="border border-violet-200 dark:border-violet-800/60 bg-violet-50/70 dark:bg-violet-950/20 rounded-lg p-3 flex items-center gap-3 flex-wrap" data-testid="sub-job-parent-banner">
      <TreeStructure size={18} className="text-violet-600 dark:text-violet-300 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-violet-900 dark:text-violet-200">Sub-job {job.sub_job_index || ""}{p.sub_job_summary?.total ? ` of ${p.sub_job_summary.total}` : ""} · part of main job <Link to={`/jobs/${job.parent_job_id}`} className="font-mono underline underline-offset-2" data-testid="sub-job-parent-link">{p.reference || job.parent_job_reference}</Link></p>
        {p.title && <p className="text-[11px] text-violet-800/80 dark:text-violet-300/80 truncate">{p.title}</p>}
        <SubJobProgressBar summary={p.sub_job_summary} className="mt-1.5 max-w-xs" />
      </div>
      <Button asChild size="sm" variant="outline" className="h-7 text-[11px]"><Link to={`/jobs/${job.parent_job_id}`}><ArrowUpRight size={12} className="mr-1" /> Open main job</Link></Button>
      <Button size="sm" variant="ghost" className="h-7 text-[11px] text-muted-foreground" onClick={detach} data-testid="sub-job-detach-btn"><LinkBreak size={12} className="mr-1" /> Detach</Button>
    </div>
  );
}

/* Main-job panel: progress, one row per sub-job, add / attach actions */
export default function SubJobsPanel({ job, onChanged }) {
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [view, setView] = useState("list");
  const child = isSubJob(job);

  const load = useCallback(() => {
    if (!job?.id || child) return;
    API.get(`/jobs/${job.id}/sub-jobs`).then(({ data }) => setData(data)).catch(() => setData({ sub_jobs: [], summary: { total: 0 } }));
  }, [job?.id, child]);
  useEffect(() => { load(); }, [load, job?.sub_job_summary?.total, job?.sub_job_summary?.completed, job?.updated_at]);

  if (child) return <ParentBanner job={job} onChanged={onChanged} />;
  if (job.contract_id || job.status === "cancelled" && !job.sub_job_summary?.total) return null;

  const refresh = () => { load(); onChanged?.(); };
  const detach = async (c) => {
    if (!window.confirm(`Detach ${c.reference} from this main job?`)) return;
    try { await API.post(`/jobs/${job.id}/sub-jobs/${c.id}/detach`); toast.success(`${c.reference} detached`); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not detach"); }
  };
  const rows = data?.sub_jobs || [];
  const summary = data?.summary || job.sub_job_summary;

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden" data-testid="sub-jobs-panel">
      <div className="p-4 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><TreeStructure size={12} /> Sub-jobs <SubJobChip summary={summary} testid="sub-jobs-chip" /></h3>
          {rows.length > 0
            ? <SubJobProgressBar summary={summary} className="mt-2 w-64 max-w-full" testid="sub-jobs-progress" />
            : <p className="text-xs text-muted-foreground mt-1.5 max-w-md">Several issues at this property? Log each one as a sub-job — assign it to an operative or supplier, track it like a normal job, and this main job completes itself when the last one is done.</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          <ProjectValue value={data?.value} />
          {rows.length > 0 && (
            <div className="inline-flex rounded-md border border-border overflow-hidden text-[11px]" data-testid="sub-jobs-view-toggle">
              <button type="button" onClick={() => setView("list")} className={`px-2.5 py-1 inline-flex items-center gap-1 ${view === "list" ? "bg-violet-600 text-white" : "hover:bg-muted"}`} data-testid="sub-jobs-view-list"><ListBullets size={12} /> List</button>
              <button type="button" onClick={() => setView("day")} className={`px-2.5 py-1 inline-flex items-center gap-1 border-l border-border ${view === "day" ? "bg-violet-600 text-white" : "hover:bg-muted"}`} data-testid="sub-jobs-view-day"><CalendarBlank size={12} /> Day plan</button>
            </div>
          )}
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setShowAttach(true)} data-testid="sub-jobs-attach-btn"><LinkSimple size={12} className="mr-1" /> Attach existing</Button>
          <Button size="sm" className="h-7 text-[11px]" onClick={() => setShowAdd(true)} data-testid="sub-jobs-add-btn"><Plus size={12} className="mr-1" /> Add sub-job</Button>
        </div>
      </div>
      {rows.length > 0 && view === "day" && <SubJobsDayPlan parentId={job.id} refreshKey={`${job.updated_at}-${rows.length}`} onChanged={refresh} />}
      {rows.length > 0 && view === "list" && (
        <>
          <p className="px-4 pb-2 -mt-1 text-[10px] text-muted-foreground" data-testid="sub-jobs-order-hint">Drag the handle (or use the row menu) to set the order the trades should attend — engineers see this sequence in their portal.</p>
          <SubJobsSortableList parentId={job.id} rows={rows} onReordered={load} onDetach={detach} />
        </>
      )}
      {summary?.total > 0 && summary.open === 0 && summary.completed > 0 && (
        <p className="px-4 py-2 text-[11px] text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border-t border-emerald-200 dark:border-emerald-800" data-testid="sub-jobs-all-done">All sub-jobs are finished — the main job was marked complete automatically.</p>
      )}
      <SubJobDialog open={showAdd} onOpenChange={setShowAdd} parent={job} onCreated={refresh} />
      <AttachJobDialog open={showAttach} onOpenChange={setShowAttach} parent={job} onAttached={refresh} />
    </div>
  );
}
